import React from 'react';
import { Alert, Button, List, Progress, Spin, Table } from 'antd';

import PlanLimitAlert from '../common/PlanLimitAlert';
import useWindowClass from '../../hooks/useWindowClass';
import { apiErrorMessage } from '../../utils/apiErrors';
import { countWords } from './importCopy';

const PAGE = 20;

const COLUMNS = [
	{ title: 'Row', dataIndex: 'number', width: 72 },
	{ title: 'Column', dataIndex: 'column', width: 180, ellipsis: true },
	{ title: 'Value', dataIndex: 'value', width: 180, ellipsis: true },
	{ title: 'What to fix', dataIndex: 'words' },
];

/** What keepr refused, row by row: a table with room for it, a list on a phone. */
export function FailureList({ lines }) {
	const { compact } = useWindowClass();
	if (!lines.length) return null;
	const pagination = { pageSize: PAGE, hideOnSinglePage: true, showSizeChanger: false };
	if (compact) {
		return (
			<List
				className="spreadsheet-import__failures"
				dataSource={lines}
				rowKey="key"
				pagination={lines.length > PAGE ? { ...pagination, size: 'small' } : false}
				renderItem={line => (
					<List.Item key={line.key}>
						<div className="spreadsheet-import__failure">
							<span className="spreadsheet-import__failure-where">{[`Row ${line.number}`, line.column].filter(Boolean).join(' · ')}</span>
							<span>{line.words}</span>
						</div>
					</List.Item>
				)}
			/>
		);
	}
	return <Table className="spreadsheet-import__failures" size="small" columns={COLUMNS} dataSource={lines} rowKey="key" pagination={pagination} />;
}

/** A determinate bar over a real count (platform-standards §14). */
export function RunProgress({ done, total, verb }) {
	const percent = total ? Math.round((done / total) * 100) : 0;
	return (
		<div className="spreadsheet-import__progress" role="status">
			<Progress percent={percent} showInfo={false} />
			<p className="spreadsheet-import__note">{`${verb} ${done.toLocaleString('en-US')} of ${countWords(total, 'row')}`}</p>
		</div>
	);
}

/**
 * Step 3: the dry run. Nothing is written until the person presses Import, and
 * what they read here is the server's answer, with every refusal on its row.
 */
export default function CheckStep({ check, summary, lines, onRetry }) {
	if (check.phase === 'error') {
		return (
			<section className="spreadsheet-import__panel" aria-label="Check">
				<Alert
					type="error"
					showIcon
					message={apiErrorMessage(check.error, 'keepr could not check these rows. Nothing was imported.')}
					action={<Button size="small" onClick={onRetry}>Try again</Button>}
				/>
			</section>
		);
	}
	if (check.phase !== 'done') {
		return (
			<section className="spreadsheet-import__panel" aria-label="Check">
				{check.phase === 'rows'
					? <RunProgress done={check.done} total={check.total} verb="Checking" />
					: <p className="spreadsheet-import__summary" role="status"><Spin size="small" /> Looking up the names in this file</p>}
			</section>
		);
	}
	const { planRefusal, signing } = check.result;
	return (
		<section className="spreadsheet-import__panel" aria-label="Check">
			<p className="spreadsheet-import__summary" aria-live="polite">{summary}</p>
			{planRefusal ? <PlanLimitAlert refusal={planRefusal} /> : null}
			{signing ? <p className="spreadsheet-import__note">This card asks for a signature. keepr asks for yours when you import.</p> : null}
			<FailureList lines={lines} />
		</section>
	);
}
